import React, { useState, Component } from 'react';
import { motion } from 'framer-motion';
import { ArrowUpRight } from 'lucide-react';
// Material icons as simple, elegant SVG components
const MaterialIcons = {
  basalt: () => <svg viewBox="0 0 24 24" fill="none" className="w-6 h-6" data-id="element-242">
      <path d="M12 2L2 12L12 22L22 12L12 2Z" stroke="currentColor" strokeWidth="1.5" fill="currentColor" fillOpacity="0.1" data-id="element-243" />
      <path d="M7 12L12 7L17 12L12 17L7 12Z" stroke="currentColor" strokeWidth="1" data-id="element-244" />
    </svg>,
  cedar: () => <svg viewBox="0 0 24 24" fill="none" className="w-6 h-6" data-id="element-245">
      <rect x="4" y="3" width="16" height="18" rx="1" stroke="currentColor" strokeWidth="1.5" data-id="element-246" />
      <line x1="4" y1="8" x2="20" y2="8" stroke="currentColor" strokeWidth="1" data-id="element-247" />
      <line x1="4" y1="13" x2="20" y2="13" stroke="currentColor" strokeWidth="1" data-id="element-248" />
      <line x1="4" y1="18" x2="20" y2="18" stroke="currentColor" strokeWidth="1" data-id="element-249" />
    </svg>,
  steel: () => <svg viewBox="0 0 24 24" fill="none" className="w-6 h-6" data-id="element-250">
      <rect x="3" y="6" width="18" height="12" stroke="currentColor" strokeWidth="1.5" data-id="element-251" />
      <line x1="3" y1="12" x2="21" y2="12" stroke="currentColor" strokeWidth="1.5" data-id="element-252" />
      <circle cx="7" cy="9" r="1" fill="currentColor" data-id="element-253" />
      <circle cx="17" cy="9" r="1" fill="currentColor" data-id="element-254" />
      <circle cx="7" cy="15" r="1" fill="currentColor" data-id="element-255" />
      <circle cx="17" cy="15" r="1" fill="currentColor" data-id="element-256" />
    </svg>,
  timber: () => <svg viewBox="0 0 24 24" fill="none" className="w-6 h-6" data-id="element-257">
      <rect x="2" y="8" width="20" height="8" stroke="currentColor" strokeWidth="1.5" data-id="element-258" />
      <ellipse cx="6" cy="12" rx="2" ry="3" stroke="currentColor" strokeWidth="1" data-id="element-259" />
      <line x1="10" y1="8" x2="10" y2="16" stroke="currentColor" strokeWidth="1" data-id="element-260" />
      <line x1="14" y1="8" x2="14" y2="16" stroke="currentColor" strokeWidth="1" data-id="element-261" />
      <line x1="18" y1="8" x2="18" y2="16" stroke="currentColor" strokeWidth="1" data-id="element-262" />
    </svg>,
  granite: () => <svg viewBox="0 0 24 24" fill="none" className="w-6 h-6" data-id="element-263">
      <polygon points="12,2 22,8 22,16 12,22 2,16 2,8" stroke="currentColor" strokeWidth="1.5" fill="currentColor" fillOpacity="0.05" data-id="element-264" />
      <circle cx="8" cy="10" r="1" fill="currentColor" fillOpacity="0.3" data-id="element-265" />
      <circle cx="14" cy="8" r="1.5" fill="currentColor" fillOpacity="0.2" data-id="element-266" />
      <circle cx="16" cy="14" r="1" fill="currentColor" fillOpacity="0.3" data-id="element-267" />
      <circle cx="10" cy="15" r="1.5" fill="currentColor" fillOpacity="0.2" data-id="element-268" />
    </svg>,
  glass: () => <svg viewBox="0 0 24 24" fill="none" className="w-6 h-6" data-id="element-269">
      <rect x="3" y="3" width="18" height="18" stroke="currentColor" strokeWidth="1.5" data-id="element-270" />
      <line x1="3" y1="12" x2="21" y2="12" stroke="currentColor" strokeWidth="1" data-id="element-271" />
      <line x1="12" y1="3" x2="12" y2="21" stroke="currentColor" strokeWidth="1" data-id="element-272" />
      <path d="M5 5L10 10" stroke="currentColor" strokeWidth="0.75" opacity="0.5" data-id="element-273" />
      <path d="M14 5L19 10" stroke="currentColor" strokeWidth="0.75" opacity="0.5" data-id="element-274" />
    </svg>
};
type MaterialType = keyof typeof MaterialIcons;
interface Material {
  type: MaterialType;
  name: string;
}
const projects = [{
  id: 1,
  title: 'The Rathdrum Ridge Estate',
  location: 'Post Falls, ID',
  description: 'Contemporary mountain modern perched above the Spokane River valley, with sweeping views of the Selkirk Range',
  image: 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=800&q=80',
  size: 'large' as const,
  year: '2025',
  sqft: '6,200',
  materials: [{
    type: 'timber' as MaterialType,
    name: 'Douglas Fir'
  }, {
    type: 'basalt' as MaterialType,
    name: 'Local Basalt'
  }, {
    type: 'glass' as MaterialType,
    name: 'Triple-Pane Glass'
  }]
}, {
  id: 2,
  title: 'Priest Lake Timber Frame',
  location: 'Priest Lake, ID',
  description: 'Massive hand-hewn beams support a steep roof pitch, anchored by a granite foundation',
  image: 'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=800&q=80',
  size: 'small' as const,
  year: '2024',
  sqft: '4,800',
  materials: [{
    type: 'timber' as MaterialType,
    name: 'Reclaimed Timber'
  }, {
    type: 'granite' as MaterialType,
    name: 'Idaho Granite'
  }, {
    type: 'cedar' as MaterialType,
    name: 'Western Red Cedar'
  }]
}, {
  id: 3,
  title: 'The Falls Overlook',
  location: 'Post Falls, ID',
  description: 'Floor-to-ceiling glass walls dissolve the boundary between interior living and the forested hillside',
  image: 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=800&q=80',
  size: 'small' as const,
  year: '2025',
  sqft: '5,400',
  materials: [{
    type: 'steel' as MaterialType,
    name: 'Weathered Steel'
  }, {
    type: 'glass' as MaterialType,
    name: 'Floor-to-Ceiling Glass'
  }, {
    type: 'cedar' as MaterialType,
    name: 'Charred Cedar'
  }]
}, {
  id: 4,
  title: 'Selkirk Ridge Residence',
  location: "Coeur d'Alene, ID",
  description: 'Perched on a rocky outcropping, heavy timber frames cantilever over the granite below',
  image: 'https://images.unsplash.com/photo-1600566753190-17f0baa2a6c3?w=800&q=80',
  size: 'wide' as const,
  year: '2024',
  sqft: '7,100',
  materials: [{
    type: 'timber' as MaterialType,
    name: 'Douglas Fir Beams'
  }, {
    type: 'steel' as MaterialType,
    name: 'Raw Steel'
  }, {
    type: 'granite' as MaterialType,
    name: 'Native Granite'
  }, {
    type: 'glass' as MaterialType,
    name: 'Structural Glass'
  }]
}, {
  id: 5,
  title: 'The Hayden Ponderosa',
  location: 'Hayden, ID',
  description: 'Nestled among towering Ponderosa pines, the steep roof echoes the surrounding peaks',
  image: 'https://images.unsplash.com/photo-1600210492486-724fe5c67fb0?w=800&q=80',
  size: 'large' as const,
  year: '2025',
  sqft: '5,800',
  materials: [{
    type: 'cedar' as MaterialType,
    name: 'Western Red Cedar'
  }, {
    type: 'basalt' as MaterialType,
    name: 'Columnar Basalt'
  }, {
    type: 'timber' as MaterialType,
    name: 'Glulam Beams'
  }]
}, {
  id: 6,
  title: 'The Spokane River Modern',
  location: 'Post Falls, ID',
  description: 'A bold contemporary home with walls of glass overlooking the Spokane River corridor',
  image: 'https://images.unsplash.com/photo-1600566753376-12c8ab7fb75b?w=800&q=80',
  size: 'small' as const,
  year: '2024',
  sqft: '4,600',
  materials: [{
    type: 'timber' as MaterialType,
    name: 'Exposed Timber'
  }, {
    type: 'steel' as MaterialType,
    name: 'Blackened Steel'
  }, {
    type: 'glass' as MaterialType,
    name: 'Panoramic Glass'
  }]
}];
function ProjectCard({
  project,
  index
}: {
  project: (typeof projects)[0];
  index: number;
}) {
  const [isHovered, setIsHovered] = useState(false);
  return <motion.div initial={{
    opacity: 0,
    y: 30
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true,
    margin: '-50px'
  }} transition={{
    duration: 0.7,
    delay: index * 0.1,
    ease: [0.25, 0.1, 0.25, 1]
  }} onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)} className={`group relative overflow-hidden bg-[#252525] cursor-pointer ${project.size === 'wide' ? 'md:col-span-2' : project.size === 'large' ? 'md:row-span-2' : ''}`} data-id="element-275">
      {/* Background Image with zoom */}
      <motion.div animate={{
      scale: isHovered ? 1.08 : 1
    }} transition={{
      duration: 0.8,
      ease: [0.25, 0.1, 0.25, 1]
    }} className="absolute inset-0" data-id="element-276">
        <img src={project.image} alt={project.title} className="w-full h-full object-cover" data-id="element-277" />
      </motion.div>

      {/* Gradient Overlays */}
      <div className="absolute inset-0 bg-gradient-to-t from-[#1A1A1A] via-[#1A1A1A]/30 to-transparent opacity-80" data-id="element-278" />
      <motion.div animate={{
      opacity: isHovered ? 0.95 : 0.7
    }} transition={{
      duration: 0.4
    }} className="absolute inset-0 bg-gradient-to-t from-[#1A1A1A] via-[#1A1A1A]/20 to-transparent" data-id="element-279" />

      {/* Content */}
      <div className="absolute inset-0 p-6 md:p-8 flex flex-col justify-between" data-id="element-280">
        {/* Top Row - Year & Sqft */}
        <div className="flex justify-between items-start" data-id="element-281">
          <span className="text-[#A07656] text-xs font-semibold tracking-wider" data-id="element-282">
            {project.year}
          </span>
          <motion.span animate={{
          opacity: isHovered ? 1 : 0.6
        }} className="text-[#D4CFC8]/60 text-xs font-medium" data-id="element-283">
            {project.sqft} SF
          </motion.span>
        </div>

        {/* Bottom Content */}
        <div data-id="element-284">
          {/* Location */}
          <motion.div animate={{
          y: isHovered ? 0 : 8,
          opacity: isHovered ? 1 : 0.7
        }} transition={{
          duration: 0.4
        }} data-id="element-285">
            <span className="text-[#A07656] text-xs uppercase tracking-[0.2em] mb-2 block" data-id="element-286">
              {project.location}
            </span>
          </motion.div>

          {/* Title */}
          <h3 className="text-xl md:text-2xl font-serif font-bold text-white mb-3 leading-tight" data-id="element-287">
            {project.title}
          </h3>

          {/* Description - shows on hover */}
          <motion.p animate={{
          opacity: isHovered ? 1 : 0,
          y: isHovered ? 0 : 10
        }} transition={{
          duration: 0.4,
          delay: 0.05
        }} className="text-[#D4CFC8]/70 text-sm mb-5 max-w-sm leading-relaxed" data-id="element-288">
            {project.description}
          </motion.p>

          {/* Material Palette - shows on hover */}
          <motion.div animate={{
          opacity: isHovered ? 1 : 0,
          y: isHovered ? 0 : 15
        }} transition={{
          duration: 0.4,
          delay: 0.1
        }} className="mb-5" data-id="element-289">
            <span className="text-[#A07656] text-[10px] uppercase tracking-[0.2em] font-semibold mb-3 block" data-id="element-290">
              Material Palette
            </span>
            <div className="flex flex-wrap gap-3" data-id="element-291">
              {project.materials.map((material, i) => {
              const IconComponent = MaterialIcons[material.type];
              return <motion.div key={material.name} initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: isHovered ? 1 : 0,
                y: isHovered ? 0 : 10
              }} transition={{
                duration: 0.3,
                delay: 0.15 + i * 0.08
              }} className="flex items-center gap-2 bg-[#1A1A1A]/60 backdrop-blur-sm px-3 py-2 border border-[#A07656]/20" data-id="element-292">
                    <div className="text-[#A07656]" data-id="element-293">
                      <IconComponent data-id="element-294" />
                    </div>
                    <span className="text-[#F5F3F0]/80 text-xs font-medium" data-id="element-295">
                      {material.name}
                    </span>
                  </motion.div>;
            })}
            </div>
          </motion.div>

          {/* View Project Link */}
          <motion.div animate={{
          opacity: isHovered ? 1 : 0,
          x: isHovered ? 0 : -10
        }} transition={{
          duration: 0.4,
          delay: 0.2
        }} data-id="element-296">
            <span className="inline-flex items-center gap-2 text-white text-sm uppercase tracking-wider font-semibold group/link" data-id="element-297">
              <span className="relative" data-id="element-298">
                View Project
                <span className="absolute bottom-0 left-0 w-0 h-[1px] bg-[#A07656] group-hover/link:w-full transition-all duration-300" data-id="element-299" />
              </span>
              <ArrowUpRight className="w-4 h-4 transition-transform group-hover/link:translate-x-0.5 group-hover/link:-translate-y-0.5" data-id="element-300" />
            </span>
          </motion.div>
        </div>
      </div>

      {/* Border accent on hover */}
      <motion.div animate={{
      opacity: isHovered ? 1 : 0
    }} transition={{
      duration: 0.3
    }} className="absolute inset-0 border-2 border-[#A07656]/40 pointer-events-none" data-id="element-301" />
    </motion.div>;
}
export function PortfolioGrid() {
  return <section className="relative py-24 md:py-32 bg-[#1A1A1A] text-[#F5F3F0]" data-id="element-302">
      {/* Subtle texture */}
      <div className="absolute inset-0 opacity-20" data-id="element-303">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,#252525_0%,transparent_50%)]" data-id="element-304" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-305">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-16 gap-6" data-id="element-306">
          <div data-id="element-307">
            <motion.span initial={{
            opacity: 0,
            y: 10
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className="text-[#A07656] uppercase tracking-[0.3em] text-xs font-semibold mb-4 block" data-id="element-308">
              Portfolio
            </motion.span>
            <motion.h2 initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.1
          }} className="text-4xl md:text-5xl lg:text-6xl font-serif font-bold mb-4" data-id="element-309">
              Selected Works
            </motion.h2>
            <motion.p initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.2
          }} className="text-[#D4CFC8]/60 max-w-lg font-light text-lg" data-id="element-310">
              Mountain Modern architecture—high-pitched gables, massive timber
              frames, and walls of glass that embrace the Idaho wilderness.
            </motion.p>
          </div>

          <motion.button initial={{
          opacity: 0
        }} whileInView={{
          opacity: 1
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.3
        }} className="hidden md:flex items-center gap-3 text-[#A07656] hover:text-[#B8896A] transition-colors uppercase tracking-widest text-sm font-semibold group" data-id="element-311">
            <span data-id="element-312">View All Projects</span>
            <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" data-id="element-313" />
          </motion.button>
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 auto-rows-[320px] md:auto-rows-[420px]" data-id="element-314">
          {projects.map((project, index) => <ProjectCard key={project.id} project={project} index={index} data-id="element-315" />)}
        </div>

        {/* Mobile CTA */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="mt-12 text-center md:hidden" data-id="element-316">
          <button className="inline-flex items-center gap-2 text-[#A07656] uppercase tracking-widest text-sm font-semibold" data-id="element-317">
            View All Projects <ArrowUpRight className="w-4 h-4" data-id="element-318" />
          </button>
        </motion.div>
      </div>
    </section>;
}